import { SlideModalContent, SlidePanels, SlidePanel } from './JourneyDetailsSlidePanels'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import { EmptyArray, last } from 'lib/array/arrayUtils'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import JourneyV2DetailsModalFlight from './JourneyV2DetailsModalFlight'
import { constructHiddenStopSegmentOriginToStop, constructHiddenStopSegmentStopToStop, constructHiddenStopSegmentStopToDestination } from './hiddenStopUtils'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import { AMADEUS, JETSTAR, TRAVEL_FUSION } from 'constants/provider'
import FlightFareRulesModalContent from 'components/Common/Modals/FlightFareRulesModal/FlightFareRulesModalContent'
import moment from 'moment'
import { isLegFlight } from 'lib/flights/flightUtils'
import Sheet from 'components/utils/Sheet'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextLink from 'LuxKit/TextLink'
import { useIntl, FormattedMessage } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import type { FlightCabinCountLabel, FlightCabinFareRuleLabel } from 'lib/flights/flightCabinCountLabels'

interface Props {
  journey?: App.JourneyV2
  leg?: App.Leg
  legIndex?: number
  fareFamily?: App.FlightFareFamily
  inclusions?: App.InclusionFeatures
  addedBaggage?: App.InclusionFeatures
  isStandaloneFlights?: boolean
  reservationInfo?: App.NDCReservationInfo
  pnrId?: string
  region?: string
  isStandalone?: boolean
  isDeparting?: boolean
  fareRulesURL: string | undefined
  fareRulesParams: any | undefined
  setFareRulesURL: (url: string) => void
  setFareRulesParams: (params: any) => void
  onShowCoverageUnforeseenEvents?: (e: React.MouseEvent) => void
  showRefundCheckbox?: boolean
  onToggleEmbedInsurance?: () => void
  areAllFlightsCovered?: boolean
  quoteTotal?: number
  withInsurance?: boolean
  cabinCountLabels?: Array<FlightCabinCountLabel>
  headingAccessory?: React.ReactNode
  cabinFareRuleLabels?: Array<FlightCabinFareRuleLabel>
}

function JourneyV2Details(props: Props) {
  const {
    journey,
    leg,
    legIndex,
    fareFamily,
    inclusions,
    addedBaggage,
    reservationInfo,
    pnrId,
    region,
    isStandalone,
    fareRulesURL,
    fareRulesParams,
    setFareRulesURL,
    setFareRulesParams,
    onShowCoverageUnforeseenEvents,
    showRefundCheckbox,
    onToggleEmbedInsurance,
    areAllFlightsCovered,
    quoteTotal,
    withInsurance,
    cabinCountLabels,
    headingAccessory,
    cabinFareRuleLabels,
  } = props

  const intl = useIntl()

  let allFlights: Array<App.LegFlight | App.FlightV2> = EmptyArray
  let firstFlight: App.LegFlight | App.FlightV2 | undefined
  let lastFlight: App.LegFlight | App.FlightV2 | undefined
  let provider: string | undefined
  if (leg) {
    allFlights = leg.flights
    firstFlight = allFlights[0]
    lastFlight = last(allFlights)
    provider = leg.provider
  } else if (journey) {
    allFlights = journey.flightGroup.flights
    firstFlight = allFlights[0]
    lastFlight = last(allFlights)
    provider = journey.provider
  }

  const stopovers = useMemo(() => {
    const stopovers: Record<string, { hours: number, minutes: number }> = {}
    if (allFlights.length > 1) {
      for (let i = 1; i < allFlights.length; i++) {
        const flight = allFlights[i]
        const previousFlight = allFlights[i - 1]
        const flightDateTime = `${flight.departingDate} ${flight.departingTime}`
        const previousFlightDateTime = `${previousFlight.arrivalDate} ${previousFlight.arrivalTime}`
        // The API reports the layover duration on the flight following the stop. Prefer it over a
        // naive diff of the per-segment times, which are local to each airport (and are placeholders
        // in the chat flight carousel), so the diff goes negative across timezones.
        const layoverTime = isLegFlight(flight) ? undefined : flight.stopoverTime
        const stopoverDiff = layoverTime
          ? layoverTime.hours * 60 + layoverTime.minutes
          : moment(flightDateTime).diff(moment(previousFlightDateTime), 'minutes')
        stopovers[i] = {
          hours: Math.floor(stopoverDiff / 60),
          minutes: stopoverDiff % 60,
        }
      }
    }
    return stopovers
  }, [allFlights])

  const amadeusFareRulesParams = useCallback(() => {
    let fareRuleParams: any = {
      provider,
      pnrId,
      region,
      isBundledWithAccommodation: !isStandalone,
    }
    if (reservationInfo?.kind === 'amadeus-ndc') {
      fareRuleParams = {
        ...fareRuleParams,
        bookingInfo: {
          carrier: reservationInfo.carrier,
          kind: reservationInfo.kind,
          orderId: reservationInfo.orderId,
        },
        legIndex,
      }
    }
    return fareRuleParams
  }, [isStandalone, pnrId, provider, region, reservationInfo, legIndex])

  const onShowFareRules = useCallback((fareRuleUrl: string) => {
    let fareRuleParams

    switch (provider) {
      case TRAVEL_FUSION:
        fareRuleParams = {
          provider,
          pnrId,
          region,
        }
        break
      case JETSTAR:
        fareRuleParams = {
          provider,
        }
        break
      case AMADEUS:
        fareRuleParams = amadeusFareRulesParams()
        break
      default:
        fareRuleParams = null
    }

    setFareRulesURL(fareRuleUrl)
    setFareRulesParams(fareRuleParams)
  }, [amadeusFareRulesParams, pnrId, provider, region, setFareRulesParams, setFareRulesURL])

  const onClickCoverageUnforeseenEvents = (e: React.MouseEvent) => {
    e.stopPropagation()
    onShowCoverageUnforeseenEvents?.(e)
  }

  const airports = useAppSelector(state => state.geo.airports)
  const hasCabinFareRuleLabels = Boolean(cabinFareRuleLabels?.length)
  let fareRulesUrls: Array<string> | undefined

  if (fareRulesURL) {
    fareRulesUrls = [fareRulesURL]

    if (hasCabinFareRuleLabels) {
      fareRulesUrls = cabinFareRuleLabels?.map(fareRuleLabel => fareRuleLabel.url)
    }
  }

  let fareRulesLabels: Array<string> | undefined
  if (hasCabinFareRuleLabels) {
    fareRulesLabels = cabinFareRuleLabels?.map(fareRuleLabel => intl.formatMessage(
      {
        id: '2HtqiKBzHNZXtXucXdOW',
        defaultMessage: '{count}x {cabin}',
        description: 'Cabin passenger count label on a My Escapes charter flight tile after a per-passenger cabin upgrade',
      },
      {
        count: fareRuleLabel.count,
        cabin: fareRuleLabel.label,
      },
    ))
  }

  return <SlideModalContent>
    <SlidePanels className={fareRulesURL ? 'right' : 'left'}>
      <SlidePanel active={!fareRulesURL}>
        <VerticalSpacer gap={12}>
          {firstFlight && lastFlight && <Heading variant="heading6">
            {intl.formatMessage({
              id: 'k0YJYWB0UhTuPqO4kNS6',
              defaultMessage: '{departureAirport} ({departureCode}) to {arrivalAirport} ({arrivalCode})',
              description: 'Heading showing flight route in details view in the flights flow',
            }, {
              departureAirport: firstFlight.departureAirportName,
              departureCode: firstFlight.departureAirport,
              arrivalAirport: lastFlight.arrivalAirportName,
              arrivalCode: lastFlight.arrivalAirport,
            })}
          </Heading>}
          {headingAccessory}
          <VerticalSpacer gap={8}>
            {allFlights.map((flight, index) => <React.Fragment key={isLegFlight(flight) ? flight.flightUniqueId : flight.id}>
              {index in stopovers && <MessageBanner icon={<LineClockFiveIcon />} kind="warning">
                {intl.formatMessage({
                  id: 'im2eAqvC2ssKq2BCncma',
                  defaultMessage: '{hours}h {minutes}m layover in {airportName} ({airportCode})',
                  description: 'Stopover duration and location in flight details in the flights section',
                }, {
                  hours: stopovers[index].hours,
                  minutes: stopovers[index].minutes,
                  airportName: flight.departureAirportName,
                  airportCode: flight.departureAirport,
                })}
              </MessageBanner>}
              <JourneyV2DetailsModalFlight
                key={`${isLegFlight(flight) ? flight.flightUniqueId : flight.id}${flight.hiddenStops?.length ? '-pre' : ''}`}
                journey={journey || leg!}
                flight={flight}
                flightIndex={index}
                fareFamily={fareFamily}
                inclusions={inclusions}
                addedBaggage={addedBaggage}
                defaultOpen={allFlights.length === 1 && (flight.hiddenStops ? flight.hiddenStops?.length === 0 : true)}
                enableViewFareRulesButton={!!pnrId}
                onShowFareRules={onShowFareRules}
                withInsurance={withInsurance}
                cabinCountLabels={cabinCountLabels}
                cabinFareRuleLabels={cabinFareRuleLabels}
                hiddenStopOverride={flight.hiddenStops?.[0] ? constructHiddenStopSegmentOriginToStop(flight.hiddenStops[0], flight, airports) : undefined}
              />
              {flight.hiddenStops?.map((hiddenStop, stopIndex) => {
                const hiddenStops = flight.hiddenStops
                const flightId = isLegFlight(flight) ? flight.flightUniqueId : flight.id
                const stopAirportName = airports.find(a => a.code === hiddenStop.airportCode)?.name ?? hiddenStop.airportCode
                const isLast = stopIndex === hiddenStops.length - 1
                const override = isLast
                  ? constructHiddenStopSegmentStopToDestination(hiddenStops, flight, airports)
                  : constructHiddenStopSegmentStopToStop(hiddenStop, hiddenStops[stopIndex + 1], airports)

                return <React.Fragment key={`${flightId}-stop-${stopIndex}`}>
                  <MessageBanner icon={<LineClockFiveIcon />} kind="warning">
                    {intl.formatMessage({
                      id: 'jySKQspg7WsfBDWkRlAm',
                      defaultMessage: '{hours}h {minutes}m Operational stop in {airportName} ({airportCode})',
                      description: 'Operational stop during a flight where passengers remain on the plane',
                    }, {
                      hours: hiddenStop.elapsedLayoverTime.hours,
                      minutes: hiddenStop.elapsedLayoverTime.minutes,
                      airportName: stopAirportName,
                      airportCode: hiddenStop.airportCode,
                    })}
                  </MessageBanner>
                  <JourneyV2DetailsModalFlight
                    journey={journey || leg!}
                    flight={flight}
                    flightIndex={index}
                    fareFamily={fareFamily}
                    inclusions={inclusions}
                    addedBaggage={addedBaggage}
                    defaultOpen={allFlights.length === 1 && hiddenStops.length === 0}
                    enableViewFareRulesButton={!!pnrId}
                    onShowFareRules={onShowFareRules}
                    withInsurance={withInsurance}
                    cabinCountLabels={cabinCountLabels}
                    cabinFareRuleLabels={cabinFareRuleLabels}
                    hiddenStopOverride={override}
                  />
                </React.Fragment>
              })}
            </React.Fragment>)}
          </VerticalSpacer>
          {showRefundCheckbox && quoteTotal && <Sheet background="normal-background" borderRadius="S" border="light-foreground" borderWidth={1} padding="24 16">
            <CheckboxInput size="medium" checked={areAllFlightsCovered} onChange={onToggleEmbedInsurance}>
              <Group direction="horizontal" gap={4} verticalAlign="start" wrap="wrap">
                <BodyText variant="medium" as="span">
                  <FormattedMessage
                    id="LNTBTaoItr0vGzlTRwgs"
                    defaultMessage="<highlight>100% refund available</highlight> from <bold>{price}</bold> for <eventsLink>unforeseen events</eventsLink>"
                    description="Insurance refund offer with price and link to unforeseen events coverage"
                    values={{
                      highlight: (chunks) => <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span">{chunks}</BodyText>,
                      bold: (chunks) => <BodyText variant="medium" weight="bold" colour="normal-contrast" as="span">{chunks}</BodyText>,
                      price: <FormatCurrency value={quoteTotal} format="roundedDollar" signDisplay="always" />,
                      eventsLink: (chunks) => <TextLink size="medium" weight="regular" underline colour="normal-contrast" onClick={onClickCoverageUnforeseenEvents}>{chunks}</TextLink>,
                    }}
                  />
                </BodyText>
              </Group>
            </CheckboxInput>
          </Sheet>}
        </VerticalSpacer>
      </SlidePanel>
      <SlidePanel active={Boolean(fareRulesURL)}>
        <FlightFareRulesModalContent
          urls={fareRulesUrls}
          labels={fareRulesLabels}
          params={fareRulesParams}
        />
      </SlidePanel>
    </SlidePanels>
  </SlideModalContent>
}

export default JourneyV2Details
